import React, { useState } from 'react';
import { Mail, Send, CheckCircle, BookOpen, Lightbulb } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const NewsletterSignup = () => {
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await axios.post(`${API_URL}/api/leads`, {
        email,
        source: 'newsletter_signup',
        offer: 'weekly_study_tips'
      });
      setIsSubmitted(true);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const tips = [
    "Weekly GCSE Maths tips & tricks",
    "Exam technique strategies",
    "Practice questions with solutions",
    "Motivation & study advice"
  ];

  return (
    <section style={{
      padding: '5rem 1.5rem',
      background: 'linear-gradient(180deg, #1E293B 0%, #0F172A 100%)'
    }}>
      <div className="container" style={{ maxWidth: '800px', margin: '0 auto' }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(13, 148, 136, 0.2) 0%, rgba(15, 118, 110, 0.2) 100%)',
          borderRadius: '24px',
          padding: '3rem 2rem',
          border: '1px solid rgba(13, 148, 136, 0.3)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Background decoration */}
          <div style={{
            position: 'absolute',
            top: '-20px',
            right: '-20px',
            width: '150px',
            height: '150px',
            background: 'rgba(255, 213, 79, 0.1)',
            borderRadius: '50%'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-30px',
            left: '-30px',
            width: '100px',
            height: '100px',
            background: 'rgba(13, 148, 136, 0.2)',
            borderRadius: '50%'
          }} />

          {!isSubmitted ? (
            <>
              {/* Header */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'rgba(255, 213, 79, 0.2)',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                marginBottom: '1.5rem'
              }}>
                <Lightbulb size={18} color="#E5C575" />
                <span style={{ color: '#E5C575', fontWeight: 600, fontSize: '0.9rem' }}>
                  Free Weekly Tips
                </span>
              </div>

              <h2 style={{
                color: 'white',
                fontSize: 'clamp(1.5rem, 4vw, 2rem)',
                fontWeight: 800,
                marginBottom: '1rem'
              }}>
                Get Smarter Every Week
              </h2>

              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                marginBottom: '2rem',
                maxWidth: '500px',
                margin: '0 auto 2rem'
              }}>
                Join 500+ students receiving free GCSE Maths tips, practice questions, and study strategies every week.
              </p>

              {/* What you'll get */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '1rem',
                marginBottom: '2rem'
              }}>
                {tips.map((tip, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'rgba(255, 255, 255, 0.1)',
                      padding: '0.5rem 1rem',
                      borderRadius: '9999px'
                    }}
                  >
                    <CheckCircle size={16} color="#C5A059" />
                    <span style={{ color: 'white', fontSize: '0.85rem' }}>{tip}</span>
                  </div>
                ))}
              </div>

              {/* Form */}
              <form 
                onSubmit={handleSubmit}
                style={{
                  display: 'flex',
                  gap: '0.75rem',
                  maxWidth: '500px',
                  margin: '0 auto',
                  flexWrap: 'wrap',
                  justifyContent: 'center'
                }}
              >
                <div style={{
                  flex: '1 1 250px',
                  position: 'relative'
                }}>
                  <Mail 
                    size={20} 
                    color="#64748B" 
                    style={{
                      position: 'absolute',
                      left: '1rem',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                  />
                  <input
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    style={{
                      width: '100%',
                      padding: '1rem 1rem 1rem 3rem',
                      borderRadius: '12px',
                      border: '2px solid transparent',
                      fontSize: '1rem',
                      outline: 'none',
                      background: 'white'
                    }}
                    data-testid="newsletter-email"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    padding: '1rem 2rem',
                    borderRadius: '12px',
                    border: 'none',
                    background: '#E5C575',
                    color: '#0F172A',
                    fontWeight: 700,
                    fontSize: '1rem',
                    cursor: isLoading ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    opacity: isLoading ? 0.7 : 1,
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isLoading) e.currentTarget.style.transform = 'scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  data-testid="newsletter-submit"
                >
                  <Send size={18} />
                  {isLoading ? 'Subscribing...' : 'Subscribe'}
                </button>
              </form>

              {error && (
                <p style={{ color: '#C5A059', fontSize: '0.9rem', marginTop: '1rem' }}>
                  {error}
                </p>
              )}

              <p style={{
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '0.8rem',
                marginTop: '1.5rem'
              }}>
                📧 Sent every Sunday. Unsubscribe anytime.
              </p>
            </>
          ) : (
            /* Success state */
            <div style={{ padding: '2rem 0' }}>
              <div style={{
                width: '80px',
                height: '80px',
                background: 'linear-gradient(135deg, #C5A059 0%, #A88B4A 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1.5rem'
              }}>
                <CheckCircle size={40} color="white" />
              </div>

              <h3 style={{
                color: 'white',
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '1rem'
              }}>
                You're In! 🎉
              </h3>

              <p style={{
                color: 'rgba(255, 255, 255, 0.8)',
                fontSize: '1.1rem',
                marginBottom: '1rem'
              }}>
                Check your inbox for a welcome email with your first study tip!
              </p>

              <div style={{
                background: 'rgba(16, 185, 129, 0.2)',
                borderRadius: '12px',
                padding: '1rem',
                maxWidth: '400px',
                margin: '0 auto'
              }}>
                <p style={{ color: '#C5A059', fontSize: '0.9rem', margin: 0 }}>
                  💡 <strong>Pro tip:</strong> Add us to your contacts so our tips don't end up in spam!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
